import { useState } from 'react';
import styled from 'styled-components';

import { ConsumptionCalculationBase } from '../../../types/ConumptionCalculationBase';
import { DemandInformationCategory, DemandInformationVessel } from '../../../types/DemandInformationMessage';
import { DiscoverableButton } from '../../cdl/Button/DiscoverableButton';
import { Collapsable } from '../../cdl/Collapsable/Collapsable';
import { PopoverClose } from '../../cdl/Popover/Popover';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../cdl/Text/Text';
import { Tooltip } from '../../cdl/Tooltip/Tooltip';
import { TextButton } from '../../common/buttons/TextButton';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { formatPort } from '../../common/helpers/formatPort.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { useRole } from '../../common/hooks/useRole';
import { Box } from '../../common/ui/Box';
import { EditPresetsOverlay } from '../../vessel/VesselConsumptionPresets/EditPresetsOverlay';
import { QualityIndicator } from '../../vessel/VesselDetailPage/AvarageConsumptionSection/components/QualityIndicator';
import { getMatchingDemandOffer } from '../utils/getMatchingDemandOffer';
import { sortCategory } from '../utils/sortCategory';

import { DemandAccordionHeader } from './DemandAccordionHeader';
import { ProductValue } from './DemandInformationTable/ProductValue';
import { StockTableHeading } from './DemandInformationTable/StockTableHeading';
import { DemandRemainingOnBoard } from './DemandRemainingOnBoard';
import { DemandTableInfoPopover } from './DemandTableInfoPopover';

interface DemandAccordionProps {
    vessel: DemandInformationVessel;
    disabled?: boolean;
}

const Wrapper = styled.div`
    &:hover ${DiscoverableButton} {
        opacity: 1;
    }
`;

export const DemandAccordion = ({ vessel, disabled = false }: DemandAccordionProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [editPresetsModalOpen, setEditPresetsModalOpen] = useState(false);
    const role = useRole();

    const sortedCategories = vessel.categories.sort(sortCategory);

    return (
        <Wrapper>
            <Collapsable
                isOpen={isOpen}
                header={
                    <DemandAccordionHeader
                        isOpen={isOpen}
                        disabled={disabled}
                        vessel={vessel}
                        onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
                    />
                }
            >
                <Collapsable.Content>
                    <Box paddingBottom={8} paddingX={8}>
                        <TableBuilder variant="inline" data={sortedCategories}>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={translate('demand.table.product')}
                                width="22%"
                            >
                                {(row) => <ProductValue row={row} vessel={vessel} />}
                            </TableBuilderColumn>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={translate('demand.table.port')}
                                width="13%"
                            >
                                {(row) => {
                                    const port = getMatchingDemandOffer(row, vessel)?.port;

                                    return port
                                        ? formatPort({ port: { ...port, locCode: port.locode }, country: false })
                                        : Characters.EN_DASH;
                                }}
                            </TableBuilderColumn>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={
                                    <DemandTableInfoPopover label={translate('demand.table.days')}>
                                        <Box>
                                            <Text variant="extraSmall" display="block">
                                                {translate('demand.table.daysPopoverLubes')}
                                            </Text>
                                            {role.hasWriteRights(vessel.customerId) ? (
                                                <PopoverClose asChild>
                                                    <TextButton onClick={() => setEditPresetsModalOpen(true)}>
                                                        {translate('consumption.editPresets.title')}
                                                    </TextButton>
                                                </PopoverClose>
                                            ) : null}
                                        </Box>
                                    </DemandTableInfoPopover>
                                }
                                numeric
                                width="9%"
                            >
                                {(row) => {
                                    const value =
                                        typeof row.daysLeft === 'number'
                                            ? formatNumber({ number: row.daysLeft, maximumFractionDigits: 0 })
                                            : Characters.EN_DASH;

                                    if (!row.consumptionCalculationBase) {
                                        return value;
                                    }

                                    return (
                                        <Tooltip
                                            label={
                                                row.consumptionCalculationBase === ConsumptionCalculationBase.STATIC
                                                    ? translate('demand.table.basedOnFixedAverage')
                                                    : translate('demand.table.basedOnCalculatedAverage')
                                            }
                                        >
                                            <Text>{value}</Text>
                                        </Tooltip>
                                    );
                                }}
                            </TableBuilderColumn>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={<StockTableHeading latestStocks={vessel.latestStocks} />}
                                numeric
                                width="13%"
                            >
                                {(row) =>
                                    typeof row?.remainingVolume === 'number' ? (
                                        <DemandRemainingOnBoard
                                            volume={row.remainingVolume}
                                            categoryState={row.categoryState}
                                            safetyReserve={row.safetyReserve}
                                            warningLimit={row.tankThreshold}
                                        />
                                    ) : (
                                        Characters.EN_DASH
                                    )
                                }
                            </TableBuilderColumn>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={
                                    <DemandTableInfoPopover label={translate('demand.table.calculatedAverage')}>
                                        <Box>
                                            <Text variant="extraSmall" display="block">
                                                {translate('demand.table.calculatedAveragePopoverText')}
                                            </Text>
                                        </Box>
                                    </DemandTableInfoPopover>
                                }
                                numeric
                                width="17%"
                            >
                                {(row) =>
                                    typeof row.calculatedAverageDailyConsumption === 'number' ? (
                                        <Box display="flex" flexDirection="row" gap={8} justifyContent="end">
                                            {formatNumber({
                                                number: row.calculatedAverageDailyConsumption,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                            {row.qualityCategory ? (
                                                <QualityIndicator qualityCategory={row.qualityCategory} />
                                            ) : null}
                                        </Box>
                                    ) : (
                                        Characters.EN_DASH
                                    )
                                }
                            </TableBuilderColumn>
                            <TableBuilderColumn<DemandInformationCategory>
                                header={
                                    <DemandTableInfoPopover label={translate('demand.table.fixedAverage')}>
                                        <Box>
                                            <Text variant="extraSmall" display="block">
                                                {translate('demand.table.fixedAveragePopoverText')}
                                            </Text>

                                            {role.hasWriteRights(vessel.customerId) ? (
                                                <PopoverClose asChild>
                                                    <TextButton onClick={() => setEditPresetsModalOpen(true)}>
                                                        {translate('consumption.editPresets.title')}
                                                    </TextButton>
                                                </PopoverClose>
                                            ) : null}
                                        </Box>
                                    </DemandTableInfoPopover>
                                }
                                numeric
                                width="13%"
                            >
                                {(row) =>
                                    typeof row.averageDailyConsumption === 'number'
                                        ? formatNumber({
                                              number: row.averageDailyConsumption,
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                          })
                                        : Characters.EN_DASH
                                }
                            </TableBuilderColumn>

                            <TableBuilderColumn<DemandInformationCategory>
                                header={translate('demand.table.robAtKeyPort')}
                                numeric
                                width="13%"
                            >
                                {(row) => {
                                    const nextKeyPortCategory = vessel.stops
                                        .find((stop) => !stop.currentPort && stop.keyPort)
                                        ?.categories.find((keyPortCategory) => {
                                            return keyPortCategory.tankCategory === row.tankCategory;
                                        });

                                    return typeof nextKeyPortCategory?.remainingVolume === 'number' ? (
                                        <DemandRemainingOnBoard
                                            volume={nextKeyPortCategory.remainingVolume}
                                            categoryState={nextKeyPortCategory.categoryState}
                                            safetyReserve={nextKeyPortCategory.safetyReserve}
                                            warningLimit={nextKeyPortCategory.tankThreshold}
                                        />
                                    ) : (
                                        Characters.EN_DASH
                                    );
                                }}
                            </TableBuilderColumn>
                        </TableBuilder>
                    </Box>
                </Collapsable.Content>
            </Collapsable>

            {editPresetsModalOpen ? (
                <EditPresetsOverlay
                    vesselId={vessel.id}
                    customerId={vessel.customerId}
                    onClose={() => setEditPresetsModalOpen(false)}
                />
            ) : null}
        </Wrapper>
    );
};
